/* ChatWidget — Intercom-style launcher + panel.
   Canonical --rv-* tokens only: the host surface (dark marketing canvas,
   light dashboard, dashboard dark mode) decides the skin. Component-scoped
   dimensions live in --cw-* custom properties on .Root. */

.Root {
  --cw-panel-width: 23.75rem;
  --cw-panel-height: 37.5rem;
  --cw-panel-gap: 1.25rem;
  --cw-launcher-size: 3.5rem;
  --cw-edge-offset: 1.25rem;
  --cw-dot-size: 0.375rem;
  --cw-input-max-h: 7.5rem;

  position: fixed;
  right: var(--cw-edge-offset);
  bottom: var(--cw-edge-offset);
  z-index: var(--rv-z-widget);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--rv-space-3);
  font-family: var(--rv-font-ui);
}

.Launcher {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--cw-launcher-size);
  height: var(--cw-launcher-size);
  border: none;
  border-radius: var(--rv-radius-full);
  background: var(--rv-accent);
  color: var(--rv-accent-ink-deep);
  cursor: pointer;
  box-shadow: var(--rv-shadow-lg);
  transition:
    transform var(--rv-dur-fast) var(--rv-ease-out),
    box-shadow var(--rv-dur-fast) var(--rv-ease-out);
}

.Launcher:hover {
  background: var(--rv-accent-hover);
  transform: translateY(-2px);
  box-shadow: var(--rv-shadow-cta-hover);
}

.Launcher:focus-visible {
  outline: none;
  box-shadow: var(--rv-shadow-ring-focus);
}

.Panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: var(--cw-panel-width);
  max-width: calc(100dvw - var(--cw-panel-gap) * 2);
  height: var(--cw-panel-height);
  max-height: calc(100dvh - var(--cw-launcher-size) - var(--cw-panel-gap) * 3);
  overflow: hidden;
  background: var(--rv-surface);
  color: var(--rv-fg-1);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-card);
  box-shadow: var(--rv-shadow-xl);
  animation: cw-panel-in var(--rv-dur-med) var(--rv-ease-out);
}

@keyframes cw-panel-in {
  from {
    opacity: 0%;
    transform: translateY(0.5rem) scale(0.98);
  }

  to {
    opacity: 100%;
    transform: translateY(0) scale(1);
  }
}

.Header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--rv-space-3);
  padding: var(--rv-space-4) var(--rv-space-5);
  border-bottom: 1px solid var(--rv-border-subtle);
}

.Title {
  margin: 0;
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-lg);
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.Subtitle {
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.IconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--rv-space-1);
  border: none;
  border-radius: var(--rv-radius-md);
  background: transparent;
  color: var(--rv-fg-3);
  cursor: pointer;
  transition: color var(--rv-dur-fast) var(--rv-ease-out);
}

.IconButton:hover {
  color: var(--rv-fg-1);
}

.Thread {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--rv-space-3);
  padding: var(--rv-space-5);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.Empty {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-4);
}

.EmptyLead {
  margin: 0;
  font-size: var(--rv-text-sm);
  line-height: var(--line-height-lg);
  color: var(--rv-fg-2);
}

.Suggestions {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  align-items: flex-start;
}

.Suggestion {
  padding: var(--rv-space-2) var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-2);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-sm);
  text-align: left;
  cursor: pointer;
  transition:
    color var(--rv-dur-fast) var(--rv-ease-out),
    border-color var(--rv-dur-fast) var(--rv-ease-out);
}

.Suggestion:hover {
  color: var(--rv-fg-1);
  border-color: var(--rv-accent);
}

.Bubble {
  max-width: 85%;
  padding: var(--rv-space-3) var(--rv-space-4);
  border-radius: var(--rv-radius-2xl);
  font-size: var(--rv-text-sm);
  line-height: var(--line-height-lg);
  overflow-wrap: break-word;
}

.BubbleUser {
  align-self: flex-end;
  background: var(--rv-accent);
  color: var(--rv-accent-ink-deep);
  border-bottom-right-radius: var(--rv-radius-sm);
}

.BubbleAssistant {
  align-self: flex-start;
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-1);
  border-bottom-left-radius: var(--rv-radius-sm);
}

.BubbleError {
  background: var(--rv-danger-bg);
  color: var(--rv-danger-ink);
}

.BubbleText {
  margin: 0;
  white-space: pre-wrap;
}

/* Rendered markdown (assistant replies). Block elements own their spacing,
   so no pre-wrap here; outer padding comes from .Bubble. Tokens only. */
.BubbleMarkdown > :first-child {
  margin-top: 0;
}

.BubbleMarkdown > :last-child {
  margin-bottom: 0;
}

.BubbleMarkdown p {
  margin: 0 0 var(--rv-space-2);
}

.BubbleMarkdown ul,
.BubbleMarkdown ol {
  margin: 0 0 var(--rv-space-2);
  padding-left: var(--rv-space-5);
}

.BubbleMarkdown li {
  margin: var(--rv-space-1) 0;
}

.BubbleMarkdown li > ul,
.BubbleMarkdown li > ol {
  margin-bottom: 0;
}

.BubbleMarkdown h1,
.BubbleMarkdown h2,
.BubbleMarkdown h3,
.BubbleMarkdown h4,
.BubbleMarkdown h5,
.BubbleMarkdown h6 {
  margin: var(--rv-space-3) 0 var(--rv-space-2);
  font-weight: var(--rv-w-semibold);
  line-height: var(--line-height-md);
}

.BubbleMarkdown h1 {
  font-size: var(--rv-text-lg);
}

.BubbleMarkdown h2 {
  font-size: var(--rv-text-md);
}

.BubbleMarkdown h3,
.BubbleMarkdown h4,
.BubbleMarkdown h5,
.BubbleMarkdown h6 {
  font-size: var(--rv-text-base);
}

.BubbleMarkdown a {
  color: var(--rv-accent);
  text-decoration: underline;
}

.BubbleMarkdown strong {
  font-weight: var(--rv-w-semibold);
}

.BubbleMarkdown code {
  padding: 0 var(--rv-space-1);
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xs);
  background: var(--rv-bg-app-preview);
  border-radius: var(--rv-radius-sm);
}

.BubbleMarkdown pre {
  margin: 0 0 var(--rv-space-2);
  padding: var(--rv-space-3);
  overflow-x: auto;
  background: var(--rv-bg-app-preview);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-md);
}

.BubbleMarkdown pre code {
  padding: 0;
  background: transparent;
  border-radius: 0;
}

.BubbleMarkdown blockquote {
  margin: 0 0 var(--rv-space-2);
  padding-left: var(--rv-space-3);
  border-left: 1px solid var(--rv-border);
  color: var(--rv-fg-2);
}

.BubbleMarkdown table {
  display: block;
  overflow-x: auto;
  border-collapse: collapse;
}

.BubbleMarkdown th,
.BubbleMarkdown td {
  padding: var(--rv-space-1) var(--rv-space-2);
  border: 1px solid var(--rv-border);
  text-align: left;
}

.Citations {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
  margin-top: var(--rv-space-3);
}

.Citation {
  padding: var(--rv-space-1) var(--rv-space-2);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-2);
  text-decoration: none;
  transition:
    color var(--rv-dur-fast) var(--rv-ease-out),
    border-color var(--rv-dur-fast) var(--rv-ease-out);
}

.Citation:hover {
  color: var(--rv-fg-1);
  border-color: var(--rv-accent);
}

/* Proposed-action confirmation cards (dashboard agent only — the docs bot
   never emits proposals). Tokens only. */
.Proposals {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  margin-top: var(--rv-space-3);
}

.Proposal {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  padding: var(--rv-space-3);
  background: var(--rv-surface);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-lg);
}

.ProposalSummary {
  margin: 0;
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.ProposalDetails {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-1);
  margin: 0;
}

.ProposalRow {
  display: flex;
  gap: var(--rv-space-2);
}

.ProposalKey {
  min-width: var(--rv-space-20);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ProposalValue {
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-1);
  overflow-wrap: anywhere;
}

.ProposalActions {
  display: flex;
  gap: var(--rv-space-2);
}

.ProposalConfirm,
.ProposalDecline {
  padding: var(--rv-space-1) var(--rv-space-3);
  border-radius: var(--rv-radius-md);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-medium);
  cursor: pointer;
  transition:
    background var(--rv-dur-fast) var(--rv-ease-out),
    border-color var(--rv-dur-fast) var(--rv-ease-out);
}

.ProposalConfirm {
  border: none;
  background: var(--rv-accent);
  color: var(--rv-accent-ink-deep);
}

.ProposalConfirm:hover:enabled {
  background: var(--rv-accent-hover);
}

.ProposalDecline {
  border: 1px solid var(--rv-border);
  background: transparent;
  color: var(--rv-fg-2);
}

.ProposalDecline:hover:enabled {
  border-color: var(--rv-accent);
  color: var(--rv-fg-1);
}

.ProposalConfirm:disabled,
.ProposalDecline:disabled {
  cursor: not-allowed;
  opacity: 60%;
}

.ProposalStatus {
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ProposalStatusError {
  color: var(--rv-danger-ink);
}

.Typing {
  display: inline-flex;
  gap: var(--rv-space-1);
}

.TypingDot {
  width: var(--cw-dot-size);
  height: var(--cw-dot-size);
  border-radius: var(--rv-radius-full);
  background: var(--rv-fg-3);
  animation: cw-typing 1.2s var(--rv-ease-in-out) infinite;
}

.TypingDot:nth-child(2) {
  animation-delay: 0.15s;
}

.TypingDot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes cw-typing {
  0%,
  60%,
  100% {
    opacity: 35%;
  }

  30% {
    opacity: 100%;
  }
}

.ComposerArea {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-1);
  padding: var(--rv-space-3) var(--rv-space-4);
  border-top: 1px solid var(--rv-border-subtle);
}

.Composer {
  display: flex;
  gap: var(--rv-space-2);
  align-items: flex-end;
}

.Input {
  flex: 1;
  min-width: 0;
  max-height: var(--cw-input-max-h);
  padding: var(--rv-space-3) var(--rv-space-4);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-1);
  font-family: var(--rv-font-ui);

  /* 16px floor — anything smaller makes iOS Safari auto-zoom the page on
     focus, which then fails to reset because this panel is fixed-position. */
  font-size: var(--rv-text-md);
  line-height: var(--line-height-lg);
  resize: none;
  overflow-y: auto;

  /* Grow with content (progressive enhancement), capped by max-height. */
  field-sizing: content;
}

.Input::placeholder {
  color: var(--rv-fg-4);
}

.Input:focus-visible {
  outline: none;
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
}

.ComposerHint {
  display: flex;
  gap: var(--rv-space-1);
  align-items: center;
  margin: 0;
  padding-left: var(--rv-space-1);
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-4);
}

.Kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--rv-space-4);
  padding: 0 var(--rv-space-1);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-sm);
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-3);
}

.HintDot {
  color: var(--rv-fg-4);
}

.SendButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--rv-space-3);
  border: none;
  border-radius: var(--rv-radius-xl);
  background: var(--rv-accent);
  color: var(--rv-accent-ink-deep);
  cursor: pointer;
  transition: background var(--rv-dur-fast) var(--rv-ease-out);
}

.SendButton:hover:enabled {
  background: var(--rv-accent-hover);
}

.SendButton:disabled {
  cursor: not-allowed;
  background: var(--rv-neutral-200);
  color: var(--rv-fg-4);
}

.CapNotice {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  align-items: flex-start;
  padding: var(--rv-space-4) var(--rv-space-5);
  border-top: 1px solid var(--rv-border-subtle);
}

.CapText {
  margin: 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ResetButton {
  padding: var(--rv-space-2) var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  background: transparent;
  color: var(--rv-fg-2);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-medium);
  cursor: pointer;
  transition: border-color var(--rv-dur-fast) var(--rv-ease-out);
}

.ResetButton:hover {
  border-color: var(--rv-accent);
  color: var(--rv-fg-1);
}

@media (width <= 30rem) {
  .Panel {
    width: calc(100dvw - var(--cw-panel-gap) * 2);
  }
}

/* ---------------- attachments (only when the host supplies them) ---------------- */

.PanelDragging {
  border-color: var(--rv-accent);
}

.DropOverlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  align-items: center;
  justify-content: center;
  border: 2px dashed var(--rv-accent);
  border-radius: var(--rv-radius-card);
  background: var(--rv-surface);
  color: var(--rv-fg-2);
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-medium);

  /* The overlay sits over the thread; it must not swallow the drop events the
     panel is listening for. */
  pointer-events: none;
}

.DropOverlay svg {
  width: var(--rv-icon-lg);
  height: var(--rv-icon-lg);
  color: var(--rv-accent);
}

.FileInput {
  display: none;
}

.AttachButton {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: var(--rv-space-3);
  border: none;
  border-radius: var(--rv-radius-xl);
  background: transparent;
  color: var(--rv-fg-3);
  cursor: pointer;
  transition: color var(--rv-dur-fast) var(--rv-ease-out);
}

.AttachButton:hover:not(:disabled) {
  color: var(--rv-fg-1);
}

.AttachButton:disabled {
  cursor: not-allowed;
  opacity: 50%;
}

.Tray {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.Chip {
  display: inline-flex;
  gap: var(--rv-space-2);
  align-items: center;
  min-width: 0;
  max-width: 100%;
  padding: var(--rv-space-1) var(--rv-space-1) var(--rv-space-1) var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-2);
  font-size: var(--rv-text-xs);
}

.ChipError {
  border-color: var(--rv-danger);
  background: var(--rv-danger-bg);
  color: var(--rv-danger-ink);
}

.AttachmentName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ChipMeta {
  flex: 0 0 auto;
  color: var(--rv-fg-3);
}

.ChipError .ChipMeta {
  color: var(--rv-danger-ink);
}

.ChipRemove {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: var(--rv-space-1);
  border: none;
  border-radius: var(--rv-radius-full);
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.ChipRemove svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.TrayError {
  margin: 0;
  color: var(--rv-danger-ink);
  font-size: var(--rv-text-xs);
}

.BubbleAttachments {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-1);
  margin: var(--rv-space-2) 0 0;
  padding: 0;
  list-style: none;
}

.BubbleAttachment {
  display: inline-flex;
  gap: var(--rv-space-1);
  align-items: center;
  min-width: 0;
  max-width: 100%;
  font-size: var(--rv-text-xs);
  opacity: 85%;
}

.BubbleAttachment svg {
  flex: 0 0 auto;
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}
